import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
} from '@mui/icons-material';
import { howToPlayAPI } from '../../../services/api';
import { toast } from 'react-toastify';

const HowToPlayManagement = () => {
  const [howToPlayContent, setHowToPlayContent] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [selectedTab, setSelectedTab] = useState('all');
  
  const [formData, setFormData] = useState({
    game_type: 'general',
    title: '',
    content: '',
    video_url: '',
    image_url: '',
    step_number: 1,
    is_active: true,
  });

  const gameTypes = [
    { value: 'general', label: 'General', color: 'primary' },
    { value: 'matka', label: 'Matka', color: 'success' },
    { value: 'gali', label: 'Gali', color: 'warning' },
  ];

  useEffect(() => {
    fetchHowToPlayContent();
  }, []);

  const fetchHowToPlayContent = async () => {
    try {
      setLoading(true);
      const response = await howToPlayAPI.getAllHowToPlay();
      if (response.data.success) {
        setHowToPlayContent(response.data.data);
      }
    } catch (error) {
      toast.error('Failed to fetch content');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateContent = () => {
    setEditingContent(null);
    setFormData({
      game_type: 'general',
      title: '',
      content: '',
      video_url: '',
      image_url: '',
      step_number: 1,
      is_active: true,
    });
    setOpenDialog(true);
  };

  const handleEditContent = (content) => {
    setEditingContent(content);
    setFormData({
      game_type: content.game_type,
      title: content.title,
      content: content.content,
      video_url: content.video_url || '',
      image_url: content.image_url || '',
      step_number: content.step_number,
      is_active: content.is_active,
    });
    setOpenDialog(true);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const submitData = {
        ...formData,
        video_url: formData.video_url || null,
        image_url: formData.image_url || null,
      };

      if (editingContent) {
        const response = await howToPlayAPI.updateHowToPlay(editingContent.id, submitData);
        if (response.data.success) {
          toast.success('Content updated successfully');
        }
      } else {
        const response = await howToPlayAPI.createHowToPlay(submitData);
        if (response.data.success) {
          toast.success('Content created successfully');
        }
      }
      
      setOpenDialog(false);
      fetchHowToPlayContent();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteContent = async (id) => {
    if (window.confirm('Are you sure you want to delete this content?')) {
      try {
        setLoading(true);
        const response = await howToPlayAPI.deleteHowToPlay(id);
        if (response.data.success) {
          toast.success('Content deleted successfully');
          fetchHowToPlayContent();
        }
      } catch (error) {
        toast.error(error.response?.data?.message || 'Delete failed');
      } finally {
        setLoading(false);
      }
    }
  };

  const getGameTypeChip = (gameType) => {
    const typeConfig = gameTypes.find(t => t.value === gameType) || gameTypes[0];
    return <Chip label={typeConfig.label} color={typeConfig.color} size="small" />;
  };

  const filteredContent = selectedTab === 'all' 
    ? howToPlayContent 
    : howToPlayContent.filter(content => content.game_type === selectedTab);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          How to Play Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateContent}
          disabled={loading}
        >
          Create Content
        </Button>
      </Box>

      <Card>
        <CardContent>
          <Box mb={3}>
            <Tabs 
              value={selectedTab} 
              onChange={(e, newValue) => setSelectedTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="All" value="all" />
              {gameTypes.map(type => (
                <Tab 
                  key={type.value}
                  label={type.label} 
                  value={type.value} 
                />
              ))}
            </Tabs>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Game Type</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Step #</TableCell>
                  <TableCell>Media</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredContent.map((content) => (
                  <TableRow key={content.id}>
                    <TableCell>{getGameTypeChip(content.game_type)}</TableCell>
                    <TableCell>
                      <Typography variant="subtitle2">{content.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {content.content.length > 50 
                          ? `${content.content.substring(0, 50)}...` 
                          : content.content}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={content.step_number} size="small" />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        {content.image_url && (
                          <Chip icon={<ImageIcon />} label="Image" size="small" />
                        )}
                        {content.video_url && (
                          <Chip icon={<VideoIcon />} label="Video" size="small" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={content.is_active ? 'Active' : 'Inactive'} 
                        color={content.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{content.creator?.username || 'System'}</TableCell>
                    <TableCell>
                      <Tooltip title="Edit">
                        <IconButton 
                          size="small" 
                          onClick={() => handleEditContent(content)}
                          disabled={loading}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleDeleteContent(content.id)}
                          disabled={loading}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredContent.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="text.secondary">No content found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingContent ? 'Edit How to Play Content' : 'Create How to Play Content'}
          <IconButton
            aria-label="close"
            onClick={() => setOpenDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Box display="flex" gap={2}>
              <FormControl fullWidth>
                <InputLabel>Game Type</InputLabel>
                <Select
                  value={formData.game_type}
                  onChange={(e) => setFormData({ ...formData, game_type: e.target.value })}
                  label="Game Type"
                >
                  {gameTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <TextField
                label="Step Number"
                type="number"
                fullWidth
                value={formData.step_number}
                onChange={(e) => setFormData({ ...formData, step_number: parseInt(e.target.value) })}
                inputProps={{ min: 1 }}
                required
              />
            </Box>
            
            <TextField
              label="Title"
              fullWidth
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
            
            <TextField
              label="Content"
              fullWidth
              multiline
              rows={6}
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              required
            />
            
            <TextField
              label="Image URL"
              fullWidth
              value={formData.image_url}
              onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
              placeholder="https://example.com/image.jpg"
            />
            
            <TextField
              label="Video URL"
              fullWidth
              value={formData.video_url}
              onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
              placeholder="https://youtube.com/watch?v=..."
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSubmit}
            disabled={loading || !formData.title || !formData.content}
          >
            {editingContent ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HowToPlayManagement;